import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  TablePagination,
  Typography,
} from "@mui/material";
import MergeIcon from "@mui/icons-material/Merge";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import useStyles from "./styles";
import { PoiReviewItem, useResolveReview, usePoiReview } from "../../api/poi";

/**
 * Haversine, client-side, so the operator can see how far apart the two
 * candidates actually are without another API round-trip. The matcher already
 * computed this server-side; showing it here is what makes the decision
 * obvious ("3 m apart, same name" vs "230 m apart, same chain").
 */
const distanceMeters = (
  a: { latitude: number | null; longitude: number | null },
  b: { latitude: number | null; longitude: number | null }
): number | null => {
  if (a.latitude == null || a.longitude == null || b.latitude == null || b.longitude == null) {
    return null;
  }

  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;

  return Math.round(2 * R * Math.asin(Math.min(1, Math.sqrt(h))));
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: "flex", gap: 1, mb: 0.5, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 76, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ minWidth: 0, wordBreak: "break-word" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const ReviewCard: React.FC<{ item: PoiReviewItem }> = ({ item }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const resolve = useResolveReview();

  const distance = item.candidate ? distanceMeters(item.incoming, item.candidate) : null;
  const score = item.match_score ?? 0;

  return (
    <Card className={classes.reviewCard}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Chip size="small" label={item.source_key} variant="outlined" />
            <Chip
              size="small"
              label={`${t("poi.score", "Score")} ${(score * 100).toFixed(0)}%`}
              color={score >= 0.82 ? "warning" : "default"}
            />
            {distance !== null && (
              <Chip size="small" variant="outlined" label={`${distance} m`} />
            )}
            {item.match_rule && (
              <Typography variant="caption" color="text.secondary" className={classes.monoSmall}>
                {item.match_rule}
              </Typography>
            )}
          </Box>
        </Box>

        <div className={classes.reviewGrid}>
          {/* Incoming */}
          <div className={classes.reviewSide}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {t("poi.incoming", "Nouvelle donnée")}
            </Typography>
            <Field label={t("poi.name", "Nom")} value={item.incoming.name} />
            <Field label={t("poi.kind", "Type")} value={item.incoming.kind} />
            <Field
              label={t("poi.address", "Adresse")}
              value={[item.incoming.street, item.incoming.postal_code, item.incoming.city]
                .filter(Boolean)
                .join(", ")}
            />
            <Field label={t("poi.phone", "Tél.")} value={item.incoming.phone} />
          </div>

          <div className={classes.reviewVs}>
            <CompareArrowsIcon color="disabled" />
          </div>

          {/* Existing candidate */}
          <div className={classes.reviewSide}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              {t("poi.existing", "Fiche existante")}
            </Typography>
            {item.candidate ? (
              <>
                <Field label={t("poi.name", "Nom")} value={item.candidate.sign_name} />
                <Field label={t("poi.kind", "Type")} value={item.candidate.kind} />
                <Field label={t("poi.phone", "Tél.")} value={item.candidate.sign_phone} />
                <Field
                  label="sign_id"
                  value={
                    <span className={classes.monoSmall}>{item.candidate.sign_id ?? "—"}</span>
                  }
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(
                  "poi.noCandidate",
                  "Aucune fiche candidate — la donnée n'a pas de coordonnées exploitables."
                )}
              </Typography>
            )}
          </div>
        </div>

        <div className={classes.reviewActions}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<MergeIcon />}
            disabled={resolve.isPending || !item.candidate}
            onClick={() =>
              resolve.mutate({
                id: item.id,
                decision: "merge",
                entity_id: item.candidate?.id,
              })
            }
            sx={{ textTransform: "none" }}
          >
            {t("poi.merge", "Même établissement")}
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<CallSplitIcon />}
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ id: item.id, decision: "separate" })}
            sx={{ textTransform: "none" }}
          >
            {t("poi.separate", "Deux établissements")}
          </Button>

          <Button
            size="small"
            variant="text"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ id: item.id, decision: "discard" })}
            sx={{ textTransform: "none" }}
          >
            {t("poi.discard", "Écarter")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const ReviewTab: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const { items, pagination, isLoading } = usePoiReview({
    page: page + 1,
    per_page: perPage,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          "poi.reviewIntro",
          "Ces rapprochements sont incertains (score entre 72 % et 87 %). Ils ne sont volontairement pas fusionnés automatiquement : une fusion erronée détruit les données des deux fiches et n'est pas réversible, alors qu'un doublon est peu coûteux."
        )}
      </Alert>

      {items.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography variant="body1">
            {t("poi.reviewEmpty", "Rien à vérifier. La file est vide.")}
          </Typography>
        </Box>
      ) : (
        <>
          {items.map((item) => (
            <ReviewCard key={item.id} item={item} />
          ))}

          <Divider sx={{ my: 2 }} />

          {pagination && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => {
                setPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage={t("poi.rowsPerPage", "Lignes par page :")}
              sx={{
                minWidth: 0,
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  rowGap: "4px",
                  minHeight: "auto",
                  paddingLeft: 0,
                },
                "& .MuiTablePagination-displayedRows": { margin: 0 },
                "& .MuiTablePagination-selectLabel": {
                  margin: 0,
                  display: { xs: "none", sm: "block" },
                },
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};
